import { cn } from "@/modules/lib/utils"
import { minScreenWidth } from "@modules/ui/uiConstants"
import { SearchIcon, XIcon } from "lucide-react"
import { type ChangeEventHandler } from "react"
import { Text } from "@/components/ui/baseUi/text"
import { InputGroup, InputGroupAddon, InputGroupInput } from "@/components/ui/baseUi/input-group"

type ScreenHeaderProps = {
  title: string
  subTitle?: string
  rightView?: React.ReactNode
  rightButton?: React.ReactNode
  children?: React.ReactNode
  className?: string
} & React.ComponentProps<"div">

export function ScreenHeader({
  title,
  subTitle,
  rightView,
  rightButton,
  children,
  className,
  ...props
}: ScreenHeaderProps) {
  return (
    <div
      className={cn(
        `mb-4 flex min-h-23 flex-row items-start justify-between gap-2 min-w-[${minScreenWidth}px]`,
        className,
      )}
      {...props}
    >
      <div className="flex flex-1 flex-col gap-0">
        <div className="flex items-center gap-1">
          <Text fontStyle="HL">{title}</Text>
          {rightButton && <div>{rightButton}</div>}
        </div>
        {subTitle && (
          <Text fontStyle="BM" className="text-textDefault-caption">
            {subTitle}
          </Text>
        )}
        {children}
      </div>
      {rightView && <>{rightView}</>}
    </div>
  )
}

type ListHeaderProps = {
  title: string
  search?: {
    onChange: ChangeEventHandler<HTMLInputElement>
    placeholder: string
    searchTerm?: string
    loading?: boolean
  }
  className?: string
} & React.ComponentProps<"div">

export function ListHeader({ title, search, className, children, ...props }: ListHeaderProps) {
  return (
    <div
      className={cn("ml-2 flex flex-col items-center gap-2 md:flex-row", className)}
      style={{ minWidth: minScreenWidth }}
      {...props}
    >
      <Text fontStyle="TS" className="grow">
        {title}
      </Text>

      <div className="flex flex-row items-center gap-2">
        {search ? (
          <InputGroup className="max-w-68">
            <InputGroupInput placeholder={search.placeholder} onChange={search.onChange} value={search.searchTerm} />
            <InputGroupAddon align="inline-start">
              <SearchIcon />
            </InputGroupAddon>
            {search.searchTerm && (
              <InputGroupAddon align="inline-end">
                <XIcon
                  className="size-4 cursor-pointer"
                  onClick={() => search.onChange({ target: { value: "" } } as React.ChangeEvent<HTMLInputElement>)}
                />
              </InputGroupAddon>
            )}
          </InputGroup>
        ) : null}

        {children}
      </div>
    </div>
  )
}
